
import { Star } from "lucide-react";

interface RatingProps {
  rating: number;
  className?: string;
  type?: "star";
  label?: string;
  gridLayout?: boolean;
  renderAsIndividualIcons?: boolean;
}

const StarRating = ({ rating, className = "", type = "star", label, gridLayout = false, renderAsIndividualIcons = false }: RatingProps) => {
  // Cap rating to max 5 stars for display
  const cappedRating = Math.min(rating, 5);
  
  // Generate an array to represent 5 stars
  const icons = [];
  
  // Calculate star color based on rating
  const getStarColor = () => {
    if (rating >= 6) return "text-green-500 fill-green-500";         // Keep rating 6 as green
    if (rating <= 1) return "text-red-500 fill-red-500";             // Keep rating 1 as red
    if (rating <= 2) return "text-orange-500 fill-orange-500";       // Orange for rating 2
    if (rating <= 3) return "text-yellow-600 fill-yellow-600";       // Bright yellow for rating 3
    if (rating <= 4) return "text-yellow-500 fill-yellow-500";       // Medium yellow for rating 4
    return "text-yellow-300 fill-yellow-300";                        // Light yellow for rating 5
  };
  
  const starColor = getStarColor();

  // Custom SVG for half star
  const HalfStar = () => {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={`${starColor} h-5 w-5`}
        aria-label="Half a star"
      >
        {/* Left half (filled) */}
        <path
          d="M12 17.8 5.8 21 7 14.1 2 9.3l7-1L12 2"
          fill="currentColor"
          stroke="none"
        />
        {/* Right half (outlined) */}
        <path
          d="M12 2v15.8 M12 17.8 18.2 21 17 14.1 22 9.3l-7-1L12 2"
          fill="transparent"
          className="stroke-current"
        />
      </svg>
    );
  };
  
  // Fill the array with full, half, or empty stars
  for (let i = 1; i <= 5; i++) {
    if (i <= cappedRating) {
      icons.push(
        <Star key={i} className={`${starColor} h-5 w-5`} />
      );
    } else if (i - 0.5 <= cappedRating) {
      icons.push(
        <HalfStar key={i} />
      );
    } else {
      icons.push(
        <Star key={i} className="text-gray-400 dark:text-gray-500 stroke-gray-400 dark:stroke-gray-500 fill-transparent h-5 w-5" />
      );
    }
  }

  if (renderAsIndividualIcons) {
    // Return individual star icons without extra divs
    return (
      <div className="flex space-x-1">
        {icons.map((icon, index) => (
          <span key={`star-wrapper-${index}`}>{icon}</span>
        ))}
      </div>
    );
  }

  return (
    <div className="flex items-center">
      <div className={`flex flex-row items-center ${className}`}>
        {icons}
      </div>
      {label && <span className="ml-2 text-sm text-muted-foreground">{label}</span>}
    </div>
  );
};

export default StarRating;
